import {
    ReactElement,
    useEffect,
    useRef,
    useState,
} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useSearchParams} from "react-router";
import {
    FuelPurchase,
    FuelPurchaseRequestBody,
    ErrorType,
    TokenPaymentMethod,
} from "@devour/client";
import {StripePaymentMethodObject} from "@/types/Stripe";
import {IStore} from "@/redux/defaultStore";
import {useGetFuelBalance} from "@/hooks/useGetFuelBalance";
import {useGetUserLevel} from "@/hooks/useGetUserLevel";
import {addError, decrementLoading, incrementLoading, makeApiError} from "@/redux/meta/metaActions";
import {useGetStripePaymentMethodList} from "@/hooks/useGetStripePaymentMethodList";
import {useUpsertFuelPurchase, useSubmitFuelPurchase, useVerifyFuelPurchase} from "@/hooks/useFuelPurchase";
import {useQueryClient} from "@tanstack/react-query";
import classNames from "classnames";
import {LoadStates} from "@/components/loadFuel/LoadStates";
import LoadFuelSuccess from "@/components/loadFuel/LoadFuelSuccess";
import CardPaymentAddModal from "@/components/modals/CardPaymentAddModal";
import LoadFuelFailedModal from "@/components/modals/LoadFuelFailedModal";
import PaymentAuthenticationModal from "@/components/modals/PaymentAuthenticationModal";
import LoadFuelConfirmModal from "@/components/modals/LoadFuelConfirmModal";
import LoadFuelSelectPayment from "@/components/loadFuel/LoadFuelSelectPayment";
import {useGetStatsigGate} from "@/hooks/useGetStatsigGate";
import {useGetUserProfile} from "@/hooks/useGetUserProfile";
import {FiX} from "react-icons/fi";

const defaultValues: FuelPurchaseRequestBody = {
    fuel: 0,
    fiat: 5,
    xp: 0,
    paymentMethod: TokenPaymentMethod.FIAT,
    paymentMethodId: "",
};

interface Props {
    toggleAsModal?: () => void;
    /**
     * Fired when a load actually COMPLETES — not when the panel closes.
     *
     * `toggleAsModal` is the X button as well as the success screen's dismiss,
     * so a caller that treated it as "fuel was loaded" reported a successful
     * reload for someone who opened the panel and closed it without paying.
     */
    onFuelLoaded?: () => void;
}

export const minXpToLoadFuel = 10;

function LoadFuelPanel(props: Props): ReactElement {
    const dispatch = useDispatch();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const {data: currentUser} = useGetUserProfile(fullToken);
    const fuelStatus = !useGetStatsigGate(import.meta.env.VITE_TOKEN_STATSIG_STATUS, false);
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const [
        errorMessage,
        setErrorMessage,
    ] = useState<string>("");
    const [
        loadState,
        setLoadState,
    ] = useState<LoadStates>(LoadStates.SELECT_PAYMENT);
    const [
        formValues,
        setFormValues,
    ] = useState<FuelPurchaseRequestBody>(defaultValues);
    const [
        fuelPurchase,
        setFuelPurchase,
    ] = useState<FuelPurchase>(undefined);
    const [
        disclaimerCheck,
        setDisclaimerCheck,
    ] = useState<boolean>(false);
    const [
        waitTimeCheck,
        setWaitTimeCheck,
    ] = useState<boolean>(false);
    const [
        countdown,
        setCountdown,
    ] = useState<string>("");
    const [
        selectedPayment,
        setSelectedPayment,
    ] = useState<StripePaymentMethodObject>(undefined);
    const [
        showAddCardModal,
        setShowAddCardModal,
    ] = useState<boolean>(false);
    const [
        showLoadFailed,
        setShowLoadFailed,
    ] = useState<boolean>(false);
    const [
        loadFailedMessage,
        setLoadFailedMessage,
    ] = useState<string>("");
    const [
        paymentNextAction,
        setPaymentNextAction,
    ] = useState<string>("");

    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

    // Monotonic quote-request stamp — see fetchPrice for why out-of-order responses are dropped.
    const quoteRequestIdRef = useRef<number>(0);

    const {
        refetch: refetchTransactionsData,
    } = useGetFuelBalance(fullToken, currentUser?.user?.id);
    const {refetch: refetchUserLevel} = useGetUserLevel(fullToken, currentUser?.user?.id);

    const {data: paymentMethodsData, refetch: refetchPaymentMethods} = useGetStripePaymentMethodList(fullToken);
    const paymentMethods = (paymentMethodsData?.paymentMethods ?? []) as Array<StripePaymentMethodObject>;

    const upsertFuelPurchaseMutation = useUpsertFuelPurchase();
    const submitFuelPurchaseMutation = useSubmitFuelPurchase();
    const verifyFuelPurchaseMutation = useVerifyFuelPurchase();

    /*
     * Auto-select a card whenever the user's payment methods load. Prefer
     * the user's `defaultPaymentMethodId` when set; otherwise fall back to
     * the first available card so the modal renders the compact "selected
     * card + Change" UI instead of flashing the expanded card list. Without
     * a fallback, an account with multiple cards but no default flagged
     * lands on the expanded list every time (legacy behavior, surfaced now).
     */
    useEffect(() => {
        if (paymentMethods.length === 0 || selectedPayment) return;
        const defaultId = currentUser?.user?.defaultPaymentMethodId;
        const card = (defaultId && paymentMethods.find(m => m.id === defaultId)) ??
            paymentMethods[0];
        if (card) {
            setSelectedPayment(card);
            void fetchPrice({paymentMethodId: card.id});
        }
    }, [paymentMethodsData, currentUser?.user?.defaultPaymentMethodId]);

    // Fetch initial price on mount
    useEffect(() => {
        void fetchPrice(formValues);
    }, [fullToken?.id]);

    useEffect(() => {
        // Not start the countdown when the page is just loaded because by the time user decides to buy FUEL, there may be not enough time
        if (!selectedPayment) return;
        const interval = setInterval(() => {
            const distance = fuelPurchase?.expiresAt - Date.now() || 0;
            if (distance > 0) {
                const minutes = Math.floor(distance % (1000 * 60 * 60) / (1000 * 60));
                const seconds = Math.floor(distance % (1000 * 60) / 1000);
                setCountdown(`${minutes}m ${seconds}s`);
            } else {
                setCountdown("");
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [
        fuelPurchase,
        selectedPayment?.id,
    ]);

    /*
     * Re-quote when re-entering the select-payment state (e.g. user
     * goes from SUCCESS back to start another purchase). Previously this
     * effect ALSO cleared `selectedPayment` — that raced with the auto-
     * select effect above on every mount, leaving selectedPayment empty
     * when the cards were already cached. Reset removed; the auto-select
     * effect owns first-card population and survives mount/unmount cycles.
     */
    useEffect(() => {
        if (loadState === LoadStates.SELECT_PAYMENT) {
            void fetchPrice(formValues);
        }
    }, [loadState]);

    /*
     * SUCCESS is the only state that means a load happened, and it is reached
     * from three places (card charge, XP, 3DS verify). Reporting it here once
     * covers all three without each one remembering to.
     */
    useEffect(() => {
        if (loadState === LoadStates.SUCCESS) {
            props.onFuelLoaded?.();
        }
    }, [loadState]);

    async function fetchPrice(values: FuelPurchaseRequestBody): Promise<void> {
        if (!fuelStatus) {
            setLoadState(LoadStates.FUEL_ERROR);
            return;
        }
        /*
         * Quotes are now requested as the user types, so responses can land out
         * of order and land after the amount has changed again. Stamp each
         * request and drop any response that a newer one has superseded.
         */
        const requestId = ++quoteRequestIdRef.current;
        dispatch(incrementLoading());
        try {
            const res = await upsertFuelPurchaseMutation.mutateAsync(values);
            if (requestId !== quoteRequestIdRef.current) {
                return;
            }
            /*
             * Merge, don't replace. A quote echoes only what the request asked
             * about, so a card-only requote comes back with no amounts — and
             * replacing would blank the FUEL figure on screen.
             */
            setFuelPurchase(prev => ({
                ...prev,
                ...res,
                fuel: res.fuel ?? prev?.fuel,
                fiat: res.fiat ?? prev?.fiat,
                xp: res.xp ?? prev?.xp,
                price: res.price ?? prev?.price,
                paymentMethod: res.paymentMethod ?? prev?.paymentMethod,
                paymentMethodId: res.paymentMethodId ?? prev?.paymentMethodId,
            }));
            setFormValues(prev => ({
                ...prev,
                fuel: res.fuel ?? prev.fuel,
                /*
                 * The response is authoritative only for the amount it was quoted
                 * with. If the user kept typing while it was in flight, keep what
                 * they typed — writing res.fiat back would yank the field to the
                 * previous value mid-entry.
                 */
                fiat: prev.fiat === values.fiat ? res.fiat ?? prev.fiat : prev.fiat,
                xp: prev.xp === values.xp ? res.xp ?? prev.xp : prev.xp,
                /*
                 * A quote only echoes back what the request asked about, and
                 * several callers requote an amount alone (`{fiat}`, `{xp}`).
                 * Coalescing keeps the card and method the user already chose
                 * instead of clearing them — submit rejects a FIAT purchase
                 * with no `paymentMethodId`.
                 */
                paymentMethod: res.paymentMethod ?? prev.paymentMethod,
                paymentMethodId: res.paymentMethodId ?? prev.paymentMethodId,
            }));
        } catch (e) {
            if (requestId !== quoteRequestIdRef.current) {
                return;
            }
            const _error = await makeApiError(e);
            setErrorMessage(_error.message);
            setLoadState(LoadStates.FUEL_ERROR);
            // Reset form value to previous value
            setFormValues(prev => ({
                ...prev,
                fuel: fuelPurchase?.fuel,
                fiat: fuelPurchase?.fiat,
                xp: fuelPurchase?.xp,
            }));
        } finally {
            setDisclaimerCheck(false);
            setWaitTimeCheck(false);
            dispatch(decrementLoading());
        }
    }

    async function onAddPaymentMethodSubmit(paymentMethodId: string): Promise<void> {
        setShowAddCardModal(false);
        // Invalidate payment methods cache so the new card appears
        queryClient.invalidateQueries({queryKey: ["stripe-payment-method-list"]});
        // Use refetch return value — paymentMethodsData closure is stale until next render
        const result = await refetchPaymentMethods();
        const freshMethods = (result.data?.paymentMethods ?? []) as Array<StripePaymentMethodObject>;
        const newCard = freshMethods.find(m => m.id === paymentMethodId);
        if (newCard) setSelectedPayment(newCard);
        await fetchPrice({paymentMethodId});
    }

    /*
     * 3-D Secure return. The issuer challenge redirects the browser to the
     * backend verify route, which bounces here with `?fuelPurchaseId=…&status=…`.
     * Re-run verify to read the OUTCOME MESSAGE from the backend rather than
     * restating the copy here — the backend owns every payment string, and a
     * message passed through the URL could be forged by anyone crafting a link.
     * Idempotent: the route's isComplete guard makes a second call a no-op.
     */
    useEffect(() => {
        const returnedPurchaseId = searchParams.get("fuelPurchaseId");
        if (!returnedPurchaseId || !fullToken?.token) {
            return;
        }

        // Strip the params first so a refresh doesn't replay the verify.
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("fuelPurchaseId");
        nextParams.delete("status");
        nextParams.delete("reason");
        setSearchParams(nextParams, {replace: true});

        void resolveThreeDSecureReturn(returnedPurchaseId);
    }, [
        searchParams,
        fullToken?.id,
    ]);

    async function resolveThreeDSecureReturn(purchaseId: string): Promise<void> {
        dispatch(incrementLoading());
        try {
            await verifyFuelPurchaseMutation.mutateAsync(purchaseId);
            await refetchTransactionsData();
            await refetchUserLevel();
            setLoadState(LoadStates.SUCCESS);
            setFormValues(defaultValues);
        } catch (e) {
            const _error = await makeApiError(e);
            setErrorMessage(_error.message);
            setLoadState(LoadStates.FUEL_ERROR);
        } finally {
            dispatch(decrementLoading());
        }
    }

    async function onPaymentVerify(displayError: boolean): Promise<void> {
        if (!fuelPurchase) {
            return;
        }
        dispatch(incrementLoading());
        try {
            await verifyFuelPurchaseMutation.mutateAsync(fuelPurchase.id);
            setPaymentNextAction("");
            await refetchTransactionsData();
            await refetchUserLevel();
            setLoadState(LoadStates.SUCCESS);
            setFormValues(defaultValues);
        } catch (e) {
            if (displayError) {
                dispatch(await addError({
                    type: ErrorType.APP,
                    message: "Payment is not complete. Please try again with a different card or contact DevourPlay support.",
                }));
            }
        } finally {
            dispatch(decrementLoading());
        }
    }

    async function onSubmit(): Promise<void> {
        dispatch(incrementLoading());
        try {
            /*
             * Every quote call above is priced but NOT persisted — the backend
             * only writes the doc when `commit` is set. Confirming is the first
             * real commitment, so the doc is created here and submitted by the
             * id that call returns.
             */
            const committed = await upsertFuelPurchaseMutation.mutateAsync({
                ...formValues,
                paymentMethodId: formValues.paymentMethodId ?? selectedPayment?.id,
                commit: true,
            });
            setFuelPurchase(committed);
            const res = await submitFuelPurchaseMutation.mutateAsync(committed.id);
            if (res.nextAction) {
                setPaymentNextAction(res.nextAction);
            } else {
                void refetchTransactionsData();
                void refetchUserLevel();
                setLoadState(LoadStates.SUCCESS);
                setFormValues(defaultValues);
            }
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : (e as unknown as { message?: string })?.message || "";
            setLoadFailedMessage(errorMessage);
            setShowLoadFailed(true);
        } finally {
            dispatch(decrementLoading());
        }
    }

    function toggleConfirmModal() {
        setShowConfirmModal(s => !s);
    }


    function renderContents() {

        switch (loadState) {
            case LoadStates.FUEL_ERROR:
                return (
                    <div>
                        {errorMessage ||
                            <>
                                {import.meta.env.VITE_TOKEN_NAME} loading is currently unavailable.<br/>
                                Please try again later or contact DevourPlay support.
                            </>
                        }

                        <button
                            type="button"
                            className="lfp2-cta"
                            onClick={() => setLoadState(LoadStates.SELECT_PAYMENT)}
                        >
                            Retry
                        </button>
                    </div>
                );
            case LoadStates.SELECT_PAYMENT:
                return (
                    <LoadFuelSelectPayment
                        fetchPrice={fetchPrice}
                        fuelPurchase={fuelPurchase}
                        selectedPayment={selectedPayment}
                        setSelectedPayment={setSelectedPayment}
                        setShowAddCardModal={setShowAddCardModal}
                        setShowConfirmModal={setShowConfirmModal}
                        disclaimerCheck={disclaimerCheck}
                        setDisclaimerCheck={setDisclaimerCheck}
                        waitTimeCheck={waitTimeCheck}
                        setWaitTimeCheck={setWaitTimeCheck}
                        formValues={formValues}
                        setFormValues={setFormValues}
                        paymentMethods={paymentMethods}
                        countdown={countdown}
                    />
                );
            default:
                return (
                    <LoadFuelSuccess
                        setLoadState={setLoadState}
                        toggleAsModal={props.toggleAsModal}
                        fuelPurchase={fuelPurchase}
                        payment={selectedPayment}
                    />
                );
        }
    }

    return (
        <>
            <CardPaymentAddModal
                isOpen={showAddCardModal}
                onClose={() => setShowAddCardModal(false)}
                onSubmit={onAddPaymentMethodSubmit}
            />
            <LoadFuelFailedModal
                isOpen={showLoadFailed}
                toggle={() => setShowLoadFailed(s => !s)}
                errorMessage={loadFailedMessage}
            />

            <LoadFuelConfirmModal
                isOpen={showConfirmModal}
                toggle={toggleConfirmModal}
                onConfirm={onSubmit}
            />

            <PaymentAuthenticationModal
                paymentNextAction={paymentNextAction}
                isOpen={!!paymentNextAction}
                onVerify={onPaymentVerify}
                onClose={() => window.location.reload()}
            />
            <div className={classNames("buy-fuel-page_panel", {
                "buy-fuel-page_panel_regular-padding": !props.toggleAsModal,
                "buy-fuel-page_panel_modal-padding": props.toggleAsModal,
            })}>
                {props.toggleAsModal &&
                    <button
                        type="button"
                        className="lfp2-modal-close"
                        onClick={props.toggleAsModal}
                        aria-label="Close"
                    >
                        <FiX aria-hidden="true" />
                    </button>
                }
                {renderContents()}
            </div>
        </>
    );
}

export default LoadFuelPanel;
